import flatten from 'lodash/flatten';

import { LegalEntityType } from 'chaika-enums';

import { LegalEntityApi } from '@organization/api/LegalEntityApi';
import { store } from '@store';
import { LegalEntityData } from 'chaika-frontend-types/frontend-cms';
import { userService } from '@user/services/userService';
import { getUniqNewChunkedIds } from '@utils/getUniqNewChunkedIds';

class LegalEntityService {
    private isLegalEntitiesLoaded = false;
    private isClinicLegalEntitiesLoaded = false;
    private isExternalClinicLegalEntitiesLoaded = false;
    private isCurrentCountryIdsLoaded = false;

    public async loadAll(): Promise<void> {
        if (!this.isLegalEntitiesLoaded) {
            const legalEntities = await LegalEntityApi.getList();

            await this.loadLegalEntitiesData(legalEntities);
            store.legalEntityList.add(legalEntities);

            this.isLegalEntitiesLoaded = true;
            this.isClinicLegalEntitiesLoaded = true;
            this.isExternalClinicLegalEntitiesLoaded = true;
        }
    }

    public async loadCurrentCountryIds(): Promise<void> {
        if (!this.isCurrentCountryIdsLoaded) {
            const legalEntities = await LegalEntityApi.getList({ onlyCurrentCountry: true });

            store.legalEntityList.setCurrentCountryIds(legalEntities.map(({ id }) => id));
            this.isCurrentCountryIdsLoaded = true;
        }
    }

    public async loadClinics(): Promise<void> {
        if (!this.isClinicLegalEntitiesLoaded) {
            const legalEntities = await LegalEntityApi.getList({ type: [LegalEntityType.Clinic].join() });

            await this.loadLegalEntitiesData(legalEntities);
            store.legalEntityList.add(legalEntities);
            this.isClinicLegalEntitiesLoaded = true;
        }
    }

    public async loadExternalClinics(): Promise<void> {
        if (!this.isExternalClinicLegalEntitiesLoaded) {
            const legalEntities = await LegalEntityApi.getList({ type: [LegalEntityType.ExternalClinic].join() });

            await this.loadLegalEntitiesData(legalEntities);
            store.legalEntityList.add(legalEntities);

            this.isExternalClinicLegalEntitiesLoaded = true;
        }
    }

    public async loadByIds(ids: string[]): Promise<void> {
        if (!this.isLegalEntitiesLoaded) {
            const uniqIds = getUniqNewChunkedIds(store.legalEntityList.ids, ids);

            const legalEntitiesChanked = await Promise.all(
                uniqIds.flatMap(newIds => LegalEntityApi.getList({ id: newIds.join() })),
            );
            const legalEntities = flatten(legalEntitiesChanked);

            await this.loadLegalEntitiesData(legalEntities);
            store.legalEntityList.add(legalEntities);
        }
    }

    public async loadForAccountant(accountantId: string[]): Promise<void> {
        if (!this.isLegalEntitiesLoaded) {
            const legalEntities = await LegalEntityApi.getList({ accountantId: accountantId.join() });

            await this.loadLegalEntitiesData(legalEntities);
            store.legalEntityList.add(legalEntities);
        }
    }

    private async loadLegalEntitiesData(legalEntities: LegalEntityData[]): Promise<void> {
        const userIds = flatten(legalEntities.map(({ accountantIds }) => accountantIds));

        await userService.loadIfNotExist(userIds);
    }
}

export const legalEntityService = new LegalEntityService();
